/**
 * --------------------------------------------------------------------------
 * Energy Saver energy-saver.js 
 * 1.1.0
 * --------------------------------------------------------------------------
 */

import * as focusTrap from 'focus-trap';
import { Idle } from 'idlejs';

const ES_CLOSE_TEMPLATE = `
    <div class="energy-saver__close">
        <button type="button" class="energy-saver__close-btn" aria-label="Close">
            <svg viewBox = "0 0 16 16" xmlns="http://www.w3.org/2000/svg" xml:space="preserve"><path d="M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z" fill="currentColor" /></svg>
        </button>
    </div>
`;

const ES_TITLE_TEMPLATE = `
    <div class="energy-saver__title">
        Rispettare l'ambiente,<br>
        anche con piccoli gesti
    </div>
`;

const ES_CONTENT_TEMPLATE = `
    <div class="energy-saver__content">
        <p>Questa schermata consente al tuo monitor di consumare meno energia quando il computer resta inattivo o quando ti allontani.</p>
        <p>Per riprendere la navigazione ti basterà cliccare un punto qualsiasi dello schermo.</p>
    </div>
`;

const ES_ICON_TEMPLATE = `
    <div class="energy-saver__ico">
        <svg viewBox="0 0 120.8 85.4" xmlns="http://www.w3.org/2000/svg" xml:space="preserve"><path d="M115.8,0.1c1.8,1.1,11.9,42.5-3.3,64.4c-11.6,16.9-30.8,25.4-45.1,18.2l44.4-76.3L62.4,79.6C49.6,69.8,48.1,48.7,59.2,31C71.8,10.9,114-1.1,115.8,0.1z" fill="currentColor" /><path d="M0.7,35.2c-1,0.9-2.8,28.2,9.2,40.5c9.2,9.4,22.4,12.7,30.7,6.6L6.7,41.4l36.7,38.3c7-7.6,5.7-21.2-3.4-31.2C29.9,37.2,1.8,34.2,0.7,35.2z"  fill="currentColor" /></svg>
    </div>
`;

const ES_ZERO_IMPACT_TEMPLATE = `
    <div class="energy-saver__zero">
        <div class="energy-saver__zero-logo">
            <img src="${window.location.protocol}//${window.location.host}/images/energy-saver/LogoLG.png" alt="" class="energy-saver__zero-logo-img" width="340" height="340" loading="lazy" decoding="async" />
        </div>
        <div class="energy-saver__zero-info">
            Grazie a LifeGate abbiamo calcolato l'impronta di carbonio imputabile allo sviluppo e alla navigazione della piattaforma del concorso che abbiamo compensato mediante crediti di carbonio certificati.
        </div>
    </div>
`;

const ES_CREDITS_TEMPLATE = `
    <div class="energy-saver__credits">
        <a href="https://www.sdm.to/" target="_blank">Made with &hearts; by sdm benefit promotion lab</a>
    </div>
`;

const ES_DEFAULT_OPTIONS = {
    idle: 300000,
    target: document.body,
    enableZeroImpact: false,
    enableZeroImpactTarget: null,
    enableCredits: false,
    closeTemplate: ES_CLOSE_TEMPLATE,
    titleTemplate: ES_TITLE_TEMPLATE,
    contentTemplate: ES_CONTENT_TEMPLATE,
    iconTemplate: ES_ICON_TEMPLATE,
    zeroImpactTemplate: ES_ZERO_IMPACT_TEMPLATE,
    creditsTemplate: ES_CREDITS_TEMPLATE,
    onShow: null,
    onHide: null,
}

const getTransitionDurationFromElement = (element) => {
    if (!element) {
        return 0;
    }

    // Get transition-duration of the element
    let { transitionDuration, transitionDelay } = window.getComputedStyle(element);

    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);

    // Return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
        return 0;
    }

    // If multiple durations are defined, take the first
    transitionDuration = transitionDuration.split(',')[0];
    transitionDelay = transitionDelay.split(',')[0];

    return (Number.parseFloat(transitionDuration) + Number.parseFloat(transitionDelay)) * 1000;
}

const triggerTransitionEnd = (element) => {
    element.dispatchEvent(new Event('transitionend'));
}

const execute = (possibleCallback, args = [], defaultValue = possibleCallback) => {
    return typeof possibleCallback === 'function' ? possibleCallback(...args) : defaultValue;
}

const executeAfterTransition = (callback, transitionElement, waitForTransition = true) => {
    if (!waitForTransition) {
        execute(callback);
        return;
    }

    const durationPadding = 5;
    const emulatedDuration = getTransitionDurationFromElement(transitionElement) + durationPadding;

    let called = false;

    const handler = ({ target }) => {
        if (target !== transitionElement) {
            return;
        }

        called = true;
        transitionElement.removeEventListener('transitionend', handler);
        execute(callback);
    }

    transitionElement.addEventListener('transitionend', handler);
    setTimeout(() => {
        if (!called) {
            triggerTransitionEnd(transitionElement);
        }
    }, emulatedDuration);
}

class EnergySaver extends EventTarget {

    // Constructor
    constructor(options) {
        super();

        this._settings = {}
;
        // Merge options
        Object.assign(
            this._settings,
            ES_DEFAULT_OPTIONS,
            options
        );

        this._active = false;
        this._parentDiv = null;
        this._trap = null;
        this._lastDocumentFocusedItem = null;
        this.#create();
        this.#start();
    }

    #emitEvent(eventName, ...args) {
        this.dispatchEvent(new CustomEvent(eventName, args));
    }

    #create = () => {
        // Add parent div
        this._parentDiv = document.createElement('div');
        this._parentDiv.classList.add('energy-saver');
        this._parentDiv.ariaLabelledby = 'energySaverLabel';
        this._parentDiv.ariaHidden = true;

        // Add top side div
        const topSideDiv = document.createElement('div');
        topSideDiv.classList.add('energy-saver__side', 'energy-saver__side--top');
        topSideDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.closeTemplate)); // Add close button
        this._parentDiv.appendChild(topSideDiv);

        // Add container div
        const containerDiv = document.createElement('div');
        containerDiv.classList.add('energy-saver__container');
        containerDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.titleTemplate)); // Add title template
        containerDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.contentTemplate)); // Add content template
        containerDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.iconTemplate)); // Add icon template
        this._settings.enableZeroImpact && containerDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.zeroImpactTemplate)); // Add zero impact template
        this._parentDiv.appendChild(containerDiv);

        // Add bottom side div
        const bottomSideDiv = document.createElement('div');
        bottomSideDiv.classList.add('energy-saver__side', 'energy-saver__side--bottom');
        if (this._settings.enableCredits) {
            bottomSideDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.creditsTemplate)); // Add credits template
        }
        this._parentDiv.appendChild(bottomSideDiv);

        // Append parent div to target div
        const target = this.#getParent(this._settings.target);
        target.appendChild(this._parentDiv);

        // Append zero impact template to zero impact target div
        const zeroImpactTarget = this.#getElement(this._settings.enableZeroImpactTarget);
        if (zeroImpactTarget && this._settings.enableZeroImpact) {
            const containerDiv = document.createElement('div');
            containerDiv.classList.add('energy-saver__container');
            containerDiv.insertAdjacentHTML('beforeend', this.#buildTemplate(this._settings.zeroImpactTemplate));
            zeroImpactTarget.appendChild(containerDiv);
        } // Add zero impact template

        // Create FocusTrap instance
        this._trap = focusTrap.createFocusTrap(this._parentDiv);
    }

    #start = () => {
        const idle = new Idle()
            .whenNotInteractive()
            .within(parseInt(this._settings.idle) ?? ES_DEFAULT_OPTIONS.idle, 1)
            .do(() => {
                if (!Boolean(this._active)) {
                    this.#show();
                }
            });
        idle.start();
    }

    #getElement = (element) => {
        try {
            return element instanceof HTMLElement ? element : document.querySelector(element);
        } catch (e) {
            return null;
        }
    }

    #getParent = (element) => {
        try {
            return element instanceof HTMLElement ? element : document.querySelector(element);
        } catch (e) {
            return this.parent;
        }
    }

    #buildTemplate = (template) => {
        if ('function' === typeof template) {
            const functionTemplate = template();
            return functionTemplate;
        } else {
            return template;
        }
    }

    #show = () => {
        this._active = true;
        this._settings.onShow && this._settings.onShow.call({}, this);
        this.#emitEvent('es.onShow');
        this._parentDiv.classList.add('fade');
        executeAfterTransition(() => {
            this._parentDiv.ariaHidden = false;
            this._parentDiv.classList.add('show');
            this._parentDiv.addEventListener('click', this.#hide, { once: true });
            this._trap.activate();
            this._lastDocumentFocusedItem = document.activeElement;
        }, this._parentDiv, true);
    }

    #hide = () => {
        this._active = false;
        this._settings.onHide && this._settings.onHide.call({}, this);
        this.#emitEvent('es.onHide');
        this._parentDiv.classList.remove('show');
        executeAfterTransition(() => {
            this._parentDiv.ariaHidden = true;
            this._trap.deactivate();
            this._parentDiv.classList.remove('fade');
            this._lastDocumentFocusedItem.focus();
        }, this._parentDiv, true);
    }

    destroy = () => {
        this._active = false;
        this._parentDiv.classList.remove('show');
        executeAfterTransition(() => {
            this._parentDiv.remove();
        }, this._parentDiv, true);
    }
}

export default EnergySaver;
